import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { _throw } from 'rxjs/observable/throw';
import { JobCardModel } from '../../../app/molecules/job-card/job-card.model';
import { INormalisedResult } from '../../../core/interfaces/interfaces';
import { ApiRequestOptions } from '../../../core/services/api/request-options';
import { File } from '../file/file.model';
import { RestrictedAccountObjectFacade } from '../restricted-account-object/restricted-account-object.facade';
import { JobApiFacade } from './job.api-facade';
import { Job } from './job.model';
import { IJobFilters } from './job.reducer';
import { JobStoreFacade } from './job.store-facade';

export class JobFacade extends RestrictedAccountObjectFacade<Job, JobApiFacade, JobStoreFacade> {

    syncAll(options?: ApiRequestOptions, accountId?: string): Observable<any> {
        if (!accountId) {
            return of();
        }
        return this.getApiAllByAccount(accountId, options)
            .take(1)
            .do(args => {
                this.updateStore(args);
            })
            .catch(error => this.handleSyncError(error));
    }

    syncOne(
        accountId: string,
        id: string,
        options?: ApiRequestOptions
    ): Observable<any> {
        return this.api.get(accountId, id, options)
            .catch(error => {
                if (error.status === 404) {
                    this.store.get(id)
                        .take(1)
                        .do(item => {
                            this.store.remove(
                                item,
                                {persistable: true, restful: false}
                            );
                        })
                        .subscribe(item => undefined);
                    return of(undefined);
                }
                return _throw(error);
            })
            .map(object => object && this.setAccountId(object, accountId))
            .do(object => object && this.updateStore([object]));
    }

    protected updateStore(remote: Array<Job>): void {

        const entities = remote
            .reduce((p, c) => {

                const result: INormalisedResult =
                    this.appFacade.normalizer.normalize(
                        c,
                        this.getSchema()
                    );

                p.job = p.job.concat(Object.values(result.job.entities));

                if (result.file) {
                    p.file = p.file.concat(Object.values(result.file.entities));
                }

                return p;

            }, {file: [], job: []});

        this.store.updateFromApi(entities.job);
        this.appFacade.objects.file.store.updateFromApi(entities.file);
    }

    protected getApiAllByAccount(
        accountId: string,
        options?: ApiRequestOptions
    ): Observable<Array<Job>> {
        return this.store
            .getFilters()
            .take(1)
            .concatMap(filters => {
                return this.api.getFiltered(accountId, filters as IJobFilters, options)
                    .map(response => response.items);
            });
    }

    onBeforeInsertIfFresher(result: INormalisedResult): void {
        throw Error('should not get here');
    }

    protected diffAndRemoveFiles(remote: Array<File>, local: Array<File>): void {
        throw Error('should not get here');
    }

    getJobCardModel(job: Job): Observable<JobCardModel> {
        return Observable.zip(
            this.appFacade.objects.accessGroup.store.getAll().take(1),
            this.appFacade.objects.account.store.getActive().take(1),
            this.appFacade.objects.user.store.getActive().take(1),
            this.appFacade.objects.control.store.getAll().take(1),
            this.appFacade.objects.field.store.getAll().take(1),
            this.appFacade.objects.file.store.getAll().take(1),
            this.appFacade.objects.jobStatus.store.getAll().take(1),
            this.appFacade.objects.jobType.store.getAll().take(1),
            this.appFacade.objects.membership.store.getActive().take(1),
            this.appFacade.objects.note.store.getAll().take(1),
            this.appFacade.objects.report.store.getAll().take(1),
            this.appFacade.objects.task.store.getAll().take(1),
            this.appFacade.objects.user.store.getAll().take(1),
            this.appFacade.network.getNetworkStatus().take(1),
            this.appFacade.objects.job.store.getPaginationModel().take(1),
            this.appFacade.objects.job.store.getUiLoading().take(1)
        )
            .map((
                [
                    accessGroups,
                    activeAccount,
                    activeUser,
                    controls,
                    fields,
                    files,
                    jobStatuses,
                    jobTypes,
                    activeMembership,
                    notes,
                    reports,
                    tasks,
                    users,
                    networkStatus
                ]) => {
                return JobCardModel.getInstance({
                    accessGroups,
                    activeAccount,
                    activeMembership,
                    activeUser,
                    controls,
                    changesPending: this.appFacade.objects.isChangePending(job.id),
                    fields,
                    files,
                    job,
                    jobStatuses,
                    jobTypes,
                    networkStatus,
                    notes,
                    reports,
                    tasks,
                    users
                });
            });
    }
}



// WEBPACK FOOTER //
// ./src/vas/business-objects/job/job.facade.ts